import { cloneDeep } from 'lodash';
import { findMatch, findSelected, hasMinimumLength } from '@/utils/resultsHelper';
import { findWithUUID } from '@/utils/uuidHelper';
import { states } from '@/constants/lifecycle';
import { dictionaryFieldTypes, dictionaryNumberTypes } from '@/constants/fields';
import { cohortChart, INVALID_CHART_SERIES_COLOUR } from './constants';
import validateClauses from '../clauses/helper';
import { findQuery } from '../state/shared';

const xAxisAndBins = ({ yAxis, bins }) => ({ fields: [yAxis.value], values: [bins.value] });

const xAndYAxis = ({ xAxis, yAxis }) => ({ fields: [xAxis.value, yAxis.value], values: [] });

const xAxisOnly = ({ xAxis }) => ({ fields: [xAxis.value], values: [] });

const visualisationVariables = {
  [cohortChart.BAR]: xAxisOnly,
  [cohortChart.BOX_PLOT]: xAndYAxis,
  [cohortChart.HISTOGRAM]: xAxisAndBins,
  [cohortChart.STACKED_BAR]: xAxisOnly,
};

const validateChartFields = (dictionaries, fields, dictionaryCode) => {
  const dictionary = dictionaries.find((r) => (r.code === dictionaryCode)) || { fields: [] };
  const names = dictionary.fields.map((f) => f.name);

  return fields.every((field) => names.includes(field));
};

const validateChartState = ({ chart }, { dictionaries }, dictionaryCode) => {
  const { fields, values } = visualisationVariables[chart.code](chart);
  return (values.every((v) => hasMinimumLength(v))) && validateChartFields(dictionaries, fields, dictionaryCode);
};

const validateCohortVisualisation = (cohort, query, chartType, dictionaryCode) => {
  const { uuid, clauses, dataset } = query;
  const visualisation = { chart: findMatch(cohort.visualisations, chartType), uuid };

  if (cohort.settings.expose_cohort_visualisations && validateChartState(visualisation, dataset, dictionaryCode)) {
    const errors = [];
    const dictionaryClauses = clauses.filter((r) => (r.dictionary.code === dictionaryCode));
    const dependentClauses = validateClauses(
      query,
      dictionaryClauses,
      errors,
      { entity: 'query', queryUUID: query.uuid, queryName: query.name },
      false,
      false,
    );
    if (errors.length === 0) {
      visualisation.clauses = dependentClauses;
    }
  }

  return visualisation;
};

// TODO A Query may have clauses over more than 1 dictionary
export const filterValidChartQueries = (cohort, chartType) => {
  const { queries, activeQueryUUID } = cohort;
  if (!queries) {
    return [];
  }

  const chartQueries = [];
  const query = findWithUUID(queries, activeQueryUUID) || { activeDictionaryCode: '' };

  queries.forEach((q) => {
    const items = q.visualisations.filter((v) => (v.code === chartType
        && validateCohortVisualisation(cohort, q, chartType, query.activeDictionaryCode).clauses));
    chartQueries.push(...items);
  });
  return chartQueries;
};

export const chartReadStatus = (chartQueries) => {
  let readStatus = null;
  let readErrors = null;
  if (chartQueries.length) {
    const pending = chartQueries.filter((q) => q.readStatus === states.PENDING);
    const success = chartQueries.filter((q) => q.readStatus === states.SUCCESS);
    const error = chartQueries.filter((q) => q.readStatus === states.ERROR);

    if (pending.length === chartQueries.length) {
      readStatus = states.PENDING;
    }
    if (success.length === chartQueries.length) {
      readStatus = states.SUCCESS;
    }
    if (error.length === chartQueries.length) {
      readStatus = states.ERROR;
      [readErrors] = [error[0].readErrors];
    }
  }

  return {
    readStatus,
    readErrors,
  };
};

export const mergeValuesDistinct = (currentValues, newValues) => [...new Set([...currentValues, ...newValues])];

export const parsePrimitiveValues = (values, intl) => {
  const mappings = [
    { value: null, translation: intl.formatMessage({ id: 'cohorts.visualisation.null.value' }) },
    { value: true, translation: intl.formatMessage({ id: 'cohorts.visualisation.true.value' }) },
    { value: false, translation: intl.formatMessage({ id: 'cohorts.visualisation.false.value' }) },
  ];
  return values.map((value) => mappings.find((m) => m.value === value)?.translation || value);
};

export const seriesTitle = (title, readStatus, intl, validQuery = true) => {
  let message = title;
  const status = readStatus || states.SUCCESS;
  if (status === states.PENDING) {
    message = intl.formatMessage({ id: 'cohort.visualisation.loading' }, { title });
  } else if (status === states.ERROR) {
    message = intl.formatMessage({ id: 'cohort.visualisation.error' }, { title });
  } else if (!validQuery) {
    message = intl.formatMessage({ id: 'cohort.visualisation.invalid' }, { title });
  }

  return message;
};

const fieldCanBeUsedInChartGrouping = (field) => (
  [dictionaryFieldTypes.TEXT, dictionaryFieldTypes.BOOL].includes(field.type) || field.constraints
);

const fieldCanBeUsedInChartScale = (field) => (
  Object.values(dictionaryNumberTypes).includes(field.type) && !field.entity && !field.constraints
);

export const filterXAxisFields = (dictFields) => dictFields.filter(fieldCanBeUsedInChartGrouping);

export const filterYAxisFields = (dictFields) => dictFields.filter(fieldCanBeUsedInChartScale);

export const chartInfo = ({
  category,
  code,
  queryUUID,
  dictionaryCode,
}, cohorts, chartType) => {
  const { cohort, query } = findQuery(cohorts, { category, code, queryUUID });
  return validateCohortVisualisation(cohort, query, chartType, dictionaryCode);
};

export const visualisationActionWithDictionary = ({
  category,
  code,
  queryUUID,
  intl,
}, cohorts) => {
  const { cohort, query } = findQuery(cohorts, { category, code, queryUUID });
  const visualisation = findSelected(cohort.visualisations)[0];

  return {
    category,
    code,
    queryUUID,
    intl,
    dictionaryCode: query.activeDictionaryCode,
    chartType: visualisation.code,
  };
};

// Due to NULL values not all histogram series will be the same length
// If 1 series is longer than the others, we can pad out with 0 for NULL
// This will be the last value in the series
export const padMissingHistogramNullValues = (series) => {
  const modified = cloneDeep(series);
  const longestRowIndex = modified.reduce((maxI, el, i, arr) => (
    ((el.data.length > arr[maxI].data.length) ? i : maxI)
  ), 0);
  const longestRow = modified[longestRowIndex];
  modified.forEach((d) => {
    if (longestRow.data.length > d.data.length) {
      d.data.push(0);
    }
  });
  return modified;
};

// Link series value to category, partly so we can make a unique key when mapping data
export const formatSeriesForTable = (series, categories) => {
  const modified = cloneDeep(series);
  modified.data = series.data.map((d, index) => ({
    category: categories[index],
    value: d,
  }));
  return modified;
};

export const isValidQueryStatus = (status) => {
  if (!status) {
    return true;
  }

  return status?.show_chart_data?.valid;
};

export const createChartSeries = (queryName, readStatus, valid, intl) => ({
  name: seriesTitle(queryName, readStatus, intl, valid),
  data: [],
  color: valid ? undefined : INVALID_CHART_SERIES_COLOUR,
});

export const createTableSeries = (queryName, readStatus, valid, intl) => ({
  data: [],
  name: seriesTitle(queryName, readStatus, intl),
  title: seriesTitle(queryName, readStatus, intl, valid),
  invalidQuery: !valid,
});

export default validateCohortVisualisation;
